import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Play, Music, Video, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RichEmbedProps {
  embedHtml: string;
  embedType?: 'player' | 'app' | 'rich' | null;
  aspectRatio?: number | null;
  embedHeight?: number | null;
  title?: string;
  siteName?: string;
  url?: string;
  provider?: string;
  className?: string;
}

// Provider icons and colors
const providerConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
  spotify: { icon: <Music className="w-4 h-4" />, color: '#1DB954', bgColor: 'bg-[#1DB954]' },
  tiktok: { icon: <Video className="w-4 h-4" />, color: '#000000', bgColor: 'bg-black' },
  youtube: { icon: <Play className="w-4 h-4" />, color: '#FF0000', bgColor: 'bg-[#FF0000]' },
  vimeo: { icon: <Play className="w-4 h-4" />, color: '#1AB7EA', bgColor: 'bg-[#1AB7EA]' },
  soundcloud: { icon: <Music className="w-4 h-4" />, color: '#FF5500', bgColor: 'bg-[#FF5500]' },
  facebook: { icon: <Globe className="w-4 h-4" />, color: '#1877F2', bgColor: 'bg-[#1877F2]' },
  instagram: { icon: <Globe className="w-4 h-4" />, color: '#E4405F', bgColor: 'bg-[#E4405F]' },
  twitter: { icon: <Globe className="w-4 h-4" />, color: '#1DA1F2', bgColor: 'bg-[#1DA1F2]' },
  x: { icon: <Globe className="w-4 h-4" />, color: '#000000', bgColor: 'bg-black' },
};

export function RichEmbed({
  embedHtml,
  embedType,
  aspectRatio,
  embedHeight,
  title,
  siteName,
  url,
  provider,
  className
}: RichEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const sanitizeIframe = (iframe: HTMLIFrameElement) => {
    // Avoid browser warning: `allow` takes precedence over `allowfullscreen`.
    if (iframe.hasAttribute('allow') && iframe.hasAttribute('allowfullscreen')) {
      iframe.removeAttribute('allowfullscreen');
    }

    // Style the iframe for responsive behavior.
    iframe.style.maxWidth = '100%';
    iframe.style.borderRadius = '12px';

    const onLoad = () => setIsLoaded(true);
    iframe.addEventListener('load', onLoad, { once: true });
  };

  useEffect(() => {
    if (!containerRef.current || !embedHtml) return;

    // Remove deprecated allowfullscreen from iframe markup before browser parses and warns.
    const sanitizedEmbedHtml = embedHtml.replace(
      /\sallowfullscreen(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?/gi,
      ''
    );

    // Clear previous content
    containerRef.current.innerHTML = '';

    // Create a wrapper div
    const wrapper = document.createElement('div');
    wrapper.innerHTML = sanitizedEmbedHtml;
    containerRef.current.appendChild(wrapper);

    // Execute any script tags in the embed HTML
    const scripts = wrapper.querySelectorAll('script');
    scripts.forEach((script) => {
      const newScript = document.createElement('script');
      
      // Copy attributes
      Array.from(script.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      
      // Copy inline script content
      if (script.textContent) {
        newScript.textContent = script.textContent;
      }
      
      // Replace old script with new one to execute it
      script.parentNode?.replaceChild(newScript, script);
    });

    // Find iframes and add load listener
    const iframes = wrapper.querySelectorAll('iframe');
    if (iframes.length > 0) {
      iframes.forEach((iframe) => {
        sanitizeIframe(iframe);
      });
    } else {
      // No iframes, consider loaded
      setIsLoaded(true);
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node.tagName.toLowerCase() === 'iframe') {
            sanitizeIframe(node as HTMLIFrameElement);
          }

          node.querySelectorAll?.('iframe').forEach((iframe) => {
            sanitizeIframe(iframe as HTMLIFrameElement);
          });
        });
      });
    });

    observer.observe(wrapper, { childList: true, subtree: true });

    return () => {
      // Cleanup
      observer.disconnect();
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [embedHtml]);

  const providerKey = provider?.toLowerCase() || siteName?.toLowerCase() || '';
  const config = providerConfig[providerKey] || { icon: <Globe className="w-4 h-4" />, color: '#666', bgColor: 'bg-gray-500' };

  // Calculate container styles based on embed type
  const getContainerStyle = () => {
    if (embedHeight) {
      return { minHeight: `${embedHeight}px` };
    }
    if (aspectRatio && aspectRatio > 0) {
      return { paddingBottom: `${(1 / aspectRatio) * 100}%` };
    }
    // Default heights for different embed types
    if (embedType === 'player') {
      return { minHeight: '352px' }; // Spotify default
    }
    return { minHeight: '400px' };
  };

  return (
    <div className={cn('rounded-xl overflow-hidden border border-border bg-card', className)}>
      {/* Provider badge */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <span className={cn('p-1.5 rounded-md text-white', config.bgColor)}>
            {config.icon}
          </span>
          <span className="text-sm font-medium text-muted-foreground">
            {siteName || provider || 'Embed'}
          </span>
        </div>
        {url && (
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* Embed container */}
      <div 
        className="relative w-full"
        style={getContainerStyle()}
      >
        {/* Loading skeleton */}
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted animate-pulse">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              {config.icon}
              <span className="text-sm">Loading {siteName || 'embed'}...</span>
            </div>
          </div>
        )}

        {/* Embed content */}
        <div 
          ref={containerRef}
          className={cn(
            'w-full transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0'
          )}
          style={{
            // Ensure iframes fit properly
            lineHeight: 0
          }}
        />
      </div>

      {/* Title (optional, shown below embed) */}
      {title && (
        <div className="px-3 py-2 border-t border-border">
          <p className="text-sm text-muted-foreground line-clamp-1">{title}</p>
        </div>
      )}
    </div>
  );
}
